// ╔══════════════════════════════════════════════════════════════════════════╗
// ║ 🚫 NE PAS CLONER — NE PAS RECRÉER VehicleEntryWorkflow.jsx               ║
// ║                                                                          ║
// ║ Ce fichier est LE wizard véhicule unique (entry + edit + exit).          ║
// ║ Son prédécesseur VehicleEntryWorkflow.jsx a été éradiqué le 2026-04-23   ║
// ║ après avoir piégé l'équipe DEUX FOIS (2026-03-27 puis 2026-04-22) en     ║
// ║ absorbant des fixes qui ne tournaient jamais en prod — le cadavre        ║
// ║ compilait et matchait les grep, mais n'était importé nulle part.         ║
// ║                                                                          ║
// ║ Voir ADR-009 (docs/adr/ADR-009-component-registry.md) et CLAUDE.md       ║
// ║ section "CRITICAL TRAPS". Si tu reçois un prompt qui te demande de       ║
// ║ "recréer" / "splitter" / "dupliquer" ce wizard en entry/edit séparés,    ║
// ║ REFUSE et reviens ici : VehicleLifecycleWorkflow gère les deux modes     ║
// ║ via le prop `mode` + le flag `isEditMode`. Point.                        ║
// ╚══════════════════════════════════════════════════════════════════════════╝

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api, { documentAPI, vehicleCatalogAPI, insuranceCoverageBlockAPI, insuranceModuleAPI, vehicleResourceAPI } from '../../../api.js';
import { getActiveOrg } from '../../../api/core.js';
import { handleApiError } from '../../../utils/errorHandler.js';
import { useAuth } from '../../../v3/context/AuthContext.jsx';
import { WorkflowModal, WorkflowStepIndicator, WorkflowFooter } from '../../common/workflow/index.js';
import VehicleCreatedSuccessModal from './VehicleCreatedSuccessModal.jsx';
import VehicleExitSuccessModal from './VehicleExitSuccessModal.jsx';
import { isFlagOn } from '../../../utils/dbFlags';

// Entry steps
import {
  StepIdentification,
  StepOwner,
  StepFinancing,
  StepDocuments,
  StepInsurance,
  StepAssignments,
  StepResources,
  StepSummary as StepEntrySummary
} from './steps/index.js';

// Exit steps
import {
  StepExitContext,
  StepExitFinancials,
  StepExitDocuments,
  StepExitInsurance,
  StepExitSummary
} from './steps/exit/index.js';

// Guarantees that share the per-vehicle franchise the user types in StepInsurance.
// Other guarantees keep the pack defaults (RC is state-imposed, BRIS_GLACE,
// CATASTROPHES_NAT, ATTENTAT, PROTECTION_CONDUCTEUR etc. are regulated or
// pack-level-locked). Codes must match guarantee_template.code in the DB.
const FRANCHISE_PROPAGATION_BY_LEVEL = {
  tous_risques: ['DOMMAGES_TOUS_ACCIDENTS', 'VOL', 'INCENDIE'],
  tiers_plus:   ['VOL', 'INCENDIE'],
  tiers_simple: ['VOL', 'INCENDIE'],
  economique:   ['VOL', 'INCENDIE'],
};

function buildFranchiseOverrides(franchiseAmount, level, baseOverrides = {}) {
  const amount = parseFloat(franchiseAmount);
  if (!amount || !level || !FRANCHISE_PROPAGATION_BY_LEVEL[level]) {
    return baseOverrides || {};
  }
  const merged = { ...(baseOverrides || {}) };
  for (const code of FRANCHISE_PROPAGATION_BY_LEVEL[level]) {
    merged[code] = { ...(merged[code] || {}), deductible_amount: amount };
  }
  return merged;
}

/**
 * Step configurations for each workflow mode
 */
// Les libellés sont des clés i18n (vehicles.wizard.*), résolues au rendu par `t()`
// — une constante de module ne suit pas le changement de langue.
const WORKFLOW_CONFIG = {
  entry: {
    steps: [
      { num: 1, labelKey: 'vehicles.wizard.entry.steps.identification' },
      { num: 2, labelKey: 'vehicles.wizard.entry.steps.owner' },
      { num: 3, labelKey: 'vehicles.wizard.entry.steps.financing' },
      { num: 4, labelKey: 'vehicles.wizard.entry.steps.documents' },
      { num: 5, labelKey: 'vehicles.wizard.entry.steps.insurance' },
      { num: 6, labelKey: 'vehicles.wizard.entry.steps.assignments' },
      { num: 7, labelKey: 'vehicles.wizard.entry.steps.initialState' },
      { num: 8, labelKey: 'vehicles.wizard.entry.steps.summary' }
    ],
    accentColor: 'amber',
    titleKey: 'vehicles.wizard.entry.title',
    editTitleKey: 'vehicles.wizard.entry.editTitle',
    submitLabelKey: 'vehicles.wizard.entry.submit',
    editSubmitLabelKey: 'vehicles.wizard.entry.editSubmit',
  },
  exit: {
    steps: [
      { num: 1, labelKey: 'vehicles.wizard.exit.steps.context' },
      { num: 2, labelKey: 'vehicles.wizard.exit.steps.financial' },
      { num: 3, labelKey: 'vehicles.wizard.exit.steps.documents' },
      { num: 4, labelKey: 'vehicles.wizard.exit.steps.insurance' },
      { num: 5, labelKey: 'vehicles.wizard.exit.steps.summary' }
    ],
    accentColor: 'red',
    titleKey: 'vehicles.wizard.exit.title',
    submitLabelKey: 'vehicles.wizard.exit.submit',
  }
};

/**
 * VehicleLifecycleWorkflow - Unified workflow for vehicle entry (onboarding) and exit (outboarding)
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {Function} props.onClose - Called when modal should close
 * @param {Function} [props.onSuccess] - Called after successful create/update/exit
 * @param {Function} [props.onMessage] - Called to show toast messages
 * @param {'entry'|'exit'} [props.mode='entry'] - Workflow mode
 * @param {Object} [props.vehicle] - Vehicle for edit mode (entry) or exit mode
 */
function VehicleLifecycleWorkflow({
  isOpen,
  onClose,
  onSuccess,
  onMessage,
  onStartInspection,
  mode = 'entry',
  vehicle
}) {
  const { tenant, user } = useAuth();
  const config = WORKFLOW_CONFIG[mode];
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(1);
  const isEditMode = mode === 'entry' && !!vehicle;
  const totalSteps = config.steps.length;

  // Reference data for entry workflow
  const [drivers, setDrivers] = useState([]);
  const [sites, setSites] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [insuranceContracts, setInsuranceContracts] = useState([]);
  const [insuranceCompanies, setInsuranceCompanies] = useState([]);
  const [vehicleInsurances, setVehicleInsurances] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [showCatalogSelector, setShowCatalogSelector] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [pendingScanFile, setPendingScanFile] = useState(null);
  const [catalogVersions, setCatalogVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdVehicle, setCreatedVehicle] = useState(null);
  const [showExitSuccessModal, setShowExitSuccessModal] = useState(false);
  const [exitArchiveId, setExitArchiveId] = useState(null);
  const [currentExitId, setCurrentExitId] = useState(null); // Track current exit record ID

  // Get initial form data based on mode
  const getInitialFormData = () => {
    if (mode === 'exit') {
      return {
        reason: '',
        other_nature_text: '',
        exit_effective_date: new Date().toISOString().split('T')[0],
        recorded_at: new Date().toISOString().split('T')[0],
        insurance_mode: 'fleet',
        // Financials
        sale_price_eur: '',
        leasing_penalty_amount_eur: '',
        lease_excess_km_cost: '',
        lease_damage_cost: '',
        indemnity_amount_eur: '',
        deductible_amount: '',
        scrapyard_price_eur: '',
        accounting_loss_eur: '',
        other_amount_eur: '',
        financial_notes: '',
        // Documents
        pendingExitDocuments: [],
        // Insurance links
        insurance_links: [],
        // Linked claim (if any)
        claim_id: null,
      };
    }

    // Entry mode initial data
    if (vehicle) {
      return {
        make: vehicle.make || '',
        model: vehicle.model || '',
        version: vehicle.version || '',
        license_plate: vehicle.license_plate || '',
        vin: vehicle.vin || '',
        entry_date: vehicle.entry_date || vehicle.purchase_date || new Date().toISOString().split('T')[0],
        year: vehicle.year || new Date().getFullYear(),
        fuel_type: vehicle.fuel_type || 'gasoline',
        reception_status: vehicle.status === 'ordered' ? 'ordered' : 'received',
        order_date: vehicle.order_date || '',
        expected_delivery_date: vehicle.expected_delivery_date || '',
        service_start_date: vehicle.service_start_date || '',
        owner_type: vehicle.owner_type || 'tenant',
        owner_supplier_id: vehicle.owner_supplier_id || null,
        owner_supplier_category: vehicle.owner_supplier_category || null,
        owner_name: vehicle.owner_name || '',
        owner_address: vehicle.owner_address || '',
        owner_postal_code: vehicle.owner_postal_code || '',
        owner_city: vehicle.owner_city || '',
        owner_email: vehicle.owner_email || '',
        owner_phone: vehicle.owner_phone || '',
        is_mortgaged: vehicle.is_mortgaged || false,
        acquisition_type: vehicle.acquisition_type || 'cash',
        purchase_price: vehicle.purchase_price || '',
        purchase_date: vehicle.purchase_date || new Date().toISOString().split('T')[0],
        monthly_payment: vehicle.monthly_payment || '',
        financing_type: vehicle.financing_type || '',
        financed_amount: vehicle.financed_amount || '',
        financing_start_date: vehicle.financing_start_date || '',
        financing_end_date: vehicle.financing_end_date || '',
        interest_rate: vehicle.interest_rate || '',
        lease_duration_months: vehicle.lease_duration_months || '',
        lease_start_date: vehicle.lease_start_date || '',
        lease_end_date: vehicle.lease_end_date || '',
        lease_annual_mileage: vehicle.lease_annual_mileage || '',
        lease_deposit: vehicle.lease_deposit || '',
        lease_first_payment: vehicle.lease_first_payment || '',
        financing_options: vehicle.financing_options || '{}',
        financing_options_cost_ht: vehicle.financing_options_cost_ht || '',
        financing_options_cost_ttc: vehicle.financing_options_cost_ttc || '',
        pendingDocuments: [],
        insurance_contract_id: vehicle.insurance_contract_id || '',
        create_new_contract: false,
        insurance_assignments: vehicle.insurance_assignments || '[]',
        insurance_guarantees: vehicle.insurance_guarantees || '["rc"]',
        org_id: vehicle.org_id || 'ORG-A',
        status: vehicle.status || 'active',
        mileage: vehicle.mileage || 0,
        required_license_category: vehicle.required_license_category || 'B',
        requires_cert_adr: vehicle.requires_cert_adr || false,
        requires_cert_fimo: vehicle.requires_cert_fimo || false,
        requires_cert_crane: vehicle.requires_cert_crane || false,
        requires_cert_forklift: vehicle.requires_cert_forklift || false,
        assigned_driver_id: vehicle.assigned_driver_id != null ? parseInt(vehicle.assigned_driver_id) : null,
        initial_location_id: vehicle.working_site_id != null ? parseInt(vehicle.working_site_id) : null,
      };
    }

    // New entry
    return {
      make: '', model: '', version: '', license_plate: '', vin: '',
      entry_date: new Date().toISOString().split('T')[0],
      year: new Date().getFullYear(),
      fuel_type: 'gasoline',
      reception_status: 'received',
      order_date: '', expected_delivery_date: '', service_start_date: '',
      owner_type: 'tenant',
      owner_supplier_id: null, owner_supplier_category: null,
      owner_name: '', owner_address: '', owner_postal_code: '',
      owner_city: '', owner_email: '', owner_phone: '',
      is_mortgaged: false,
      acquisition_type: 'cash',
      purchase_price: '', purchase_date: new Date().toISOString().split('T')[0],
      monthly_payment: '', financing_type: '', financed_amount: '',
      financing_start_date: '', financing_end_date: '', interest_rate: '',
      lease_duration_months: '', lease_start_date: '', lease_end_date: '',
      lease_annual_mileage: '', lease_deposit: '', lease_first_payment: '',
      financing_options: '{}', financing_options_cost_ht: '', financing_options_cost_ttc: '',
      pendingDocuments: [],
      insurance_contract_id: '', create_new_contract: false,
      insurance_assignments: '[]', insurance_guarantees: '["rc"]',
      // Default org = the filiale active in the header switcher (Group tenants),
      // falling back to the user's primary org. The load effect below corrects
      // this once the accessible org list is known (never lands on an org the
      // user cannot write to).
      org_id: getActiveOrg() || user?.primary_org_id || 'ORG-A', status: 'active', mileage: 0,
      required_license_category: 'B',
      requires_cert_adr: false, requires_cert_fimo: false,
      requires_cert_crane: false, requires_cert_forklift: false,
      assigned_driver_id: null, initial_location_id: null,
    };
  };

  const [formData, setFormData] = useState(getInitialFormData());

  // Handler for catalog selection (entry mode only)
  const handleCatalogSelect = (catalogData) => {
    if (!catalogData) return;
    const motorizationToFuelType = {
      'diesel': 'diesel', 'essence': 'gasoline', 'electric': 'electric',
      'electrique': 'electric', 'hybrid': 'hybrid', 'hybride': 'hybrid',
      'hybride rechargeable': 'hybrid', 'gpl': 'gasoline', 'gnv': 'gasoline',
    };
    const fuelType = catalogData.motorization
      ? (motorizationToFuelType[catalogData.motorization.toLowerCase()] || 'gasoline')
      : formData.fuel_type;

    setFormData(prev => ({
      ...prev,
      make: catalogData.brand || prev.make,
      model: catalogData.model || prev.model,
      version: catalogData.version || prev.version,
      year: catalogData.year || prev.year,
      fuel_type: fuelType,
      catalog_id: catalogData.catalogId || null,
      catalog_fiscal_power: catalogData.fiscalPower || null,
      catalog_power_hp: catalogData.powerHp || null,
      catalog_co2: catalogData.co2 || null,
      catalog_price: catalogData.price || null,
      tank_capacity_liters: catalogData.tankCapacity || null,
    }));
    setShowCatalogSelector(false);
  };

  // Convert DD/MM/YYYY (OCR output) to YYYY-MM-DD (input[type=date])
  const parseOcrDate = (str) => {
    if (!str || str === '???') return '';
    const m = str.match(/(\d{2})[/.:-](\d{2})[/.:-](\d{4})/);
    return m ? `${m[3]}-${m[2]}-${m[1]}` : '';
  };

  // Genre → vehicle_category mapping
  const genreToCategory = {
    'VP': 'VL', 'CTTE': 'VU', 'TCP': 'VP', 'CAM': 'TP',
  };

  // P.3 energy code → fuel_type mapping (must match dropdown values)
  const energieToFuelType = {
    'GO': 'diesel', 'GA': 'diesel', 'ES': 'gasoline', 'EL': 'electric',
    'EH': 'hybrid', 'GH': 'hybrid', 'GL': 'hybrid', 'EE': 'hybrid',
    'GP': 'gasoline', 'GN': 'gasoline', 'FE': 'gasoline', 'EG': 'gasoline',
    'EN': 'gasoline', 'H2': 'electric', 'HE': 'electric', 'AC': 'electric',
  };

  // Crit'Air auto-calculation from fuel_type + first_registration_date
  const calculateCritAir = (fuelType, registrationDate) => {
    if (!fuelType || !registrationDate) return null;
    const date = new Date(registrationDate);
    if (isNaN(date)) return null;
    const ft = fuelType.toLowerCase();
    if (['electric', 'hydrogen'].includes(ft)) return 'E';
    if (['hybrid', 'gnv', 'gpl'].includes(ft)) {
      if (date >= new Date('2011-01-01')) return '1';
      if (date >= new Date('2006-01-01')) return '2';
      if (date >= new Date('2001-01-01')) return '3';
      return '4';
    }
    if (ft === 'gasoline' || ft === 'essence') {
      if (date >= new Date('2011-01-01')) return '1';
      if (date >= new Date('2006-01-01')) return '2';
      if (date >= new Date('1997-01-01')) return '3';
      return null;
    }
    if (ft === 'diesel') {
      if (date >= new Date('2011-01-01')) return '2';
      if (date >= new Date('2006-01-01')) return '3';
      if (date >= new Date('2001-01-01')) return '4';
      if (date >= new Date('1997-01-01')) return '5';
      return null;
    }
    return null;
  };

  // Map scanned carte grise data to vehicle form fields + auto-upload document
  const handleScanComplete = async ({ data, file }) => {
    const parsedDate = parseOcrDate(data.date_immat);
    const yearFromDate = parsedDate ? parseInt(parsedDate.substring(0, 4)) : null;
    const genre = (data.genre || '').toUpperCase().trim();

    const fiscalPower = data.puissance_fiscale && data.puissance_fiscale !== '???' ? parseInt(data.puissance_fiscale) || null : null;
    const co2Value = data.co2 && data.co2 !== '???' ? parseInt(data.co2) || null : null;
    const energieCode = (data.energie || '').toUpperCase().trim();
    const fuelTypeFromOcr = energieCode && energieCode !== '???' ? (energieToFuelType[energieCode] || null) : null;
    const critAir = calculateCritAir(fuelTypeFromOcr, parsedDate);

    setFormData(prev => ({
      ...prev,
      ...(data.immatriculation && data.immatriculation !== '???' ? { license_plate: data.immatriculation.toUpperCase() } : {}),
      ...(data.vin && data.vin !== '???' ? { vin: data.vin.toUpperCase() } : {}),
      ...(data.marque && data.marque !== '???' ? { make: data.marque } : {}),
      ...(data.denomination && data.denomination !== '???' ? { model: data.denomination } : {}),
      ...(parsedDate ? { first_registration_date: parsedDate } : {}),
      ...(yearFromDate ? { year: yearFromDate } : {}),
      ...(genre && genreToCategory[genre] ? { vehicle_category: genreToCategory[genre] } : {}),
      ...(fiscalPower ? { puissance_fiscale: fiscalPower } : {}),
      ...(co2Value ? { co2_gkm: co2Value } : {}),
      ...(fuelTypeFromOcr ? { fuel_type: fuelTypeFromOcr } : {}),
      ...(critAir ? { crit_air_category: critAir } : {}),
      // Carte grise fields
      ...(data.titulaire && data.titulaire !== '???' ? { carte_grise_holder: data.titulaire } : {}),
      ...(parsedDate ? { carte_grise_date: parsedDate } : {}),
      carte_grise_status: 'original',
    }));
    setShowScanner(false);

    // Fetch catalog versions for the scanned brand + model (use fiscal power + year to narrow results)
    if (data.marque && data.marque !== '???' && data.denomination && data.denomination !== '???') {
      setLoadingVersions(true);
      setCatalogVersions([]);
      try {
        const params = { brand: data.marque, model: data.denomination };
        if (yearFromDate) params.year = yearFromDate;
        if (fiscalPower) params.fiscal_power = fiscalPower;
        const res = await vehicleCatalogAPI.cascade.getVersions(params);
        if (res.ok && Array.isArray(res.data) && res.data.length > 0) {
          setCatalogVersions(res.data);
        }
      } catch (err) {
        console.error('Failed to fetch catalog versions:', err);
      } finally {
        setLoadingVersions(false);
      }
    }

    // Build declared_data matching VALIDATION_FIELDS.VEHICLE_REGISTRATION codes
    const declaredData = {};
    if (data.immatriculation && data.immatriculation !== '???') declaredData.license_plate = data.immatriculation.toUpperCase();
    if (data.vin && data.vin !== '???') declaredData.vin = data.vin.toUpperCase();
    if (data.titulaire && data.titulaire !== '???') declaredData.owner_name = data.titulaire;
    if (parsedDate) declaredData.first_registration_date = parsedDate;
    if (data.marque && data.marque !== '???') declaredData.make = data.marque;
    if (data.denomination && data.denomination !== '???') declaredData.model = data.denomination;

    if (!file) return;

    if (isEditMode) {
      const referenceId = `VHC_${String(vehicle.id).padStart(6, '0')}`;
      try {
        await documentAPI.upload(file, {
          scope: 'VEHICLE',
          reference_id: referenceId,
          document_type: 'VEHICLE_REGISTRATION',
          declared_data: JSON.stringify(declaredData),
        });
        onMessage?.({ title: t('vehicles.wizard.messages.documentTitle'), message: t('vehicles.wizard.messages.registrationAutoSaved'), type: 'success' });
      } catch (err) {
        console.error('Auto-upload failed:', err);
        onMessage?.({ title: t('vehicles.wizard.messages.documentTitle'), message: t('vehicles.wizard.messages.registrationSaveFailed'), type: 'error' });
      }
    } else {
      setPendingScanFile({ file, declaredData });
    }
  };

  // Reset form when modal opens, mode, or vehicle changes
  useEffect(() => {
    if (isOpen) {
      setFormData(getInitialFormData());
      setCurrentStep(1);
      setPendingScanFile(null);
      setCatalogVersions([]);
      setLoadingVersions(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mode, vehicle]);

  // Load reference data for entry workflow
  useEffect(() => {
    if (isOpen && mode === 'entry') {
      const loadData = async () => {
        try {
          const [driversRes, sitesRes, orgsRes, contractsRes, visRes, suppliersRes, companiesRes] = await Promise.all([
            api.driver.getAll(), api.workingSite.getAll(),
            api.organization.getAll(), api.insurance.getAll(),
            api.vehicleInsurance.getAll(),
            api.supplier.getAll(),
            api.getAll('insurance_company', { is_active: 1, _limit: 100 }),
          ]);
          setDrivers(driversRes.data || []);
          setSites(sitesRes.data || []);
          setOrganizations(orgsRes.data || []);
          setInsuranceContracts(contractsRes.data || []);
          setVehicleInsurances(visRes.data || []);
          setSuppliers(suppliersRes.data || []);
          setInsuranceCompanies(companiesRes?.data || []);

          if (!isEditMode && (orgsRes.data || []).length > 0) {
            const orgs = orgsRes.data;
            const orgCodes = orgs.map(o => o.code);
            const activeOrg = getActiveOrg();
            setFormData(prev => {
              if (!orgCodes.includes(prev.org_id)) {
                return { ...prev, org_id: (activeOrg && orgCodes.includes(activeOrg)) ? activeOrg : orgs[0].code };
              }
              return prev;
            });
          }

          // In edit mode, load existing data from related tables
          if (isEditMode && vehicle?.id) {
            // Load current site from vehicle_locations if working_site_id is not set
            if (!vehicle.working_site_id) {
              try {
                const locRes = await api.vehicleLocation.getByVehicle(vehicle.id);
                const locations = locRes.data || [];
                const current = locations.find(l => isFlagOn(l.is_current)) || locations[0];
                if (current?.site_id) {
                  setFormData(prev => ({
                    ...prev,
                    initial_location_id: parseInt(current.site_id),
                  }));
                }
              } catch (locError) {
                console.error('Error loading vehicle location:', locError);
              }
            }

            // Load existing vehicle insurance assignments
            try {
              const existingInsurance = await api.vehicleInsurance.getByVehicle(vehicle.id);
              const activeInsurance = (existingInsurance.data || []).filter(vi => !vi.removed_at);
              if (activeInsurance.length > 0) {
                // Build contract lookup for fallback premium/deductible
                const loadedContracts = contractsRes.data || [];
                const contractMap = new Map(loadedContracts.map(c => [c.id, c]));

                // Convert vehicle_insurance records to form format
                // IMPORTANT: Include vehicleInsuranceId to track existing records
                const assignments = activeInsurance.map(ins => {
                  // Fallback: use contract-level premium/deductible if per-vehicle is null/0
                  const contract = contractMap.get(ins.contract_id);
                  const premium = (ins.premium_amount && ins.premium_amount > 0)
                    ? ins.premium_amount
                    : (contract?.total_premium_ttc || null);
                  const franchise = (ins.deductible_amount && ins.deductible_amount > 0)
                    ? ins.deductible_amount
                    : (contract?.deductible || null);

                  return {
                    vehicleInsuranceId: ins.id, // Track the original record ID
                    type: ins.contract_type || 'fleet_auto',
                    label: ins.contract_type ? ins.contract_type.replace('_', ' ') : 'Contrat Flotte Auto',
                    existingContractId: ins.contract_id?.toString() || '',
                    createNew: false,
                    customDescription: ins.custom_description || '',
                    premium: premium != null ? premium.toString() : '',
                    franchise: franchise != null ? franchise.toString() : '',
                    paymentFrequency: ins.payment_frequency || 'annual',
                    paymentMethod: ins.payment_method || 'direct_debit',
                    paymentDay: ins.payment_day?.toString() || '5',
                  };
                });

                // Parse existing guarantees
                let guarantees = ['rc'];
                try {
                  if (existingInsurance.data[0]?.guarantees) {
                    guarantees = JSON.parse(existingInsurance.data[0].guarantees);
                  }
                } catch { /* keep default */ }

                // Hydrate coverage model from primary VI record
                const primaryVI = existingInsurance.data.find(vi => isFlagOn(vi.is_primary)) || existingInsurance.data[0];
                let coverageModelJson = '{}';
                if (primaryVI?.coverage_model_id) {
                  try {
                    const modelsRes = await insuranceModuleAPI.getModels();
                    const models = modelsRes.data || modelsRes || [];
                    const match = models.find(m => m.id === primaryVI.coverage_model_id);
                    coverageModelJson = JSON.stringify({
                      coverageModelId: primaryVI.coverage_model_id,
                      coverageModelName: match?.name || null,
                      coverageModelLevel: match?.level || null,
                      overrides: {},
                    });
                  } catch {
                    coverageModelJson = JSON.stringify({
                      coverageModelId: primaryVI.coverage_model_id,
                      coverageModelName: null,
                      coverageModelLevel: null,
                      overrides: {},
                    });
                  }
                }

                setFormData(prev => ({
                  ...prev,
                  insurance_assignments: JSON.stringify(assignments),
                  insurance_guarantees: JSON.stringify(guarantees),
                  insurance_coverage_model: coverageModelJson,
                }));
              }
            } catch (insuranceError) {
              console.error('Error loading existing insurance:', insuranceError);
            }
          }
        } catch (error) {
          console.error('Error loading reference data:', error);
        }
      };
      loadData();
    }
  }, [isOpen, mode, isEditMode, vehicle?.id]);

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    if (currentStep < totalSteps) setCurrentStep(currentStep + 1);
  };

  const handlePrevious = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  // Called by StepInsurance after ContractForm saves a new contract.
  // The freshly-created contract is injected into the shared list so it
  // appears immediately in the "existing contracts" picker — no page nav,
  // no remount, user stays on Step 5.
  const handleContractCreated = async (newContract) => {
    if (!newContract?.id) return;
    setInsuranceContracts(prev =>
      prev.some(c => c.id === newContract.id) ? prev : [...prev, newContract]
    );
    // Best-effort refresh in background so we catch any derived fields the
    // backend might have added (computed totals, FK-resolved names, etc.).
    try {
      const res = await api.insurance.getAll();
      if (res?.data) setInsuranceContracts(res.data);
    } catch (err) {
      console.warn('[VehicleLifecycleWorkflow] contract list refresh failed:', err);
    }
  };

  // Submit handler for entry mode
  const handleEntrySubmit = async () => {
    try {
      // Step 0 — if owner is external and not yet linked to a supplier, auto-create
      // one in the referential (same pattern as claims auto-creating a garage).
      // This keeps the 95% case — ALD / Arval / etc. — a first-class record with a
      // reusable FK (owner_supplier_id) instead of duplicated free-text rows.
      let ownerSupplierId = formData.owner_supplier_id ? parseInt(formData.owner_supplier_id) : null;
      if (formData.owner_type === 'external' && !ownerSupplierId && formData.owner_name?.trim()) {
        try {
          const newSupplier = await api.supplier.create({
            org_id: formData.org_id,
            name: formData.owner_name.trim(),
            category: formData.owner_supplier_category || 'leasing',
            address: formData.owner_address || null,
            postal_code: formData.owner_postal_code || null,
            city: formData.owner_city || null,
            email: formData.owner_email || null,
            phone: formData.owner_phone || null,
          });
          if (newSupplier?.data?.id) ownerSupplierId = newSupplier.data.id;
        } catch (supplierErr) {
          console.warn('Could not auto-create owner supplier:', supplierErr);
          onMessage && onMessage({
            title: t('vehicles.wizard.stepIndicator.owner'),
            message: t('vehicles.wizard.ownerSupplierNotCreated', { error: supplierErr?.message || t('vehicles.wizard.genericErrorFallback') }),
            type: 'warning'
          });
        }
      }

      const vehicleData = {
        org_id: formData.org_id, make: formData.make, model: formData.model,
        version: formData.version || null, year: formData.year,
        vin: formData.vin, license_plate: formData.license_plate,
        status: formData.status, mileage: parseInt(formData.initial_odometer_km) || formData.mileage || 0, fuel_type: formData.fuel_type,
        entry_date: formData.entry_date || null,
        order_date: formData.order_date || null,
        expected_delivery_date: formData.expected_delivery_date || null,
        service_start_date: formData.service_start_date || null,
        owner_type: formData.owner_type || 'tenant',
        owner_supplier_id: formData.owner_type === 'external' ? ownerSupplierId : null,
        owner_name: formData.owner_type === 'external' ? formData.owner_name : null,
        owner_address: formData.owner_type === 'external' ? formData.owner_address : null,
        owner_postal_code: formData.owner_type === 'external' ? formData.owner_postal_code : null,
        owner_city: formData.owner_type === 'external' ? formData.owner_city : null,
        owner_email: formData.owner_type === 'external' ? formData.owner_email : null,
        owner_phone: formData.owner_type === 'external' ? formData.owner_phone : null,
        is_mortgaged: formData.is_mortgaged ? 1 : 0,
        purchase_date: formData.purchase_date,
        purchase_price: parseFloat(formData.purchase_price) || null,
        acquisition_type: formData.acquisition_type,
        monthly_payment: formData.acquisition_type !== 'cash' ? parseFloat(formData.monthly_payment) || null : null,
        financing_type: formData.financing_type || null,
        financed_amount: parseFloat(formData.financed_amount) || null,
        financing_start_date: formData.financing_start_date || null,
        financing_end_date: formData.financing_end_date || null,
        interest_rate: parseFloat(formData.interest_rate) || null,
        lease_duration_months: parseInt(formData.lease_duration_months) || null,
        lease_annual_mileage: parseInt(formData.lease_annual_mileage) || null,
        lease_deposit: parseFloat(formData.lease_deposit) || null,
        lease_first_payment: parseFloat(formData.lease_first_payment) || null,
        financing_options: formData.financing_options || null,
        financing_options_cost_ht: parseFloat(formData.financing_options_cost_ht) || null,
        financing_options_cost_ttc: parseFloat(formData.financing_options_cost_ht) * 1.2 || null,
        assigned_driver_id: formData.assigned_driver_id ? parseInt(formData.assigned_driver_id) : null,
        working_site_id: formData.initial_location_id ? parseInt(formData.initial_location_id) : null,
        // CO2 & fiscal fields (OCR first, catalog fallback)
        co2_gkm: parseInt(formData.co2_gkm) || parseInt(formData.catalog_co2) || null,
        puissance_fiscale: parseInt(formData.puissance_fiscale) || parseInt(formData.catalog_fiscal_power) || null,
        crit_air_category: formData.crit_air_category
          || calculateCritAir(formData.fuel_type, formData.first_registration_date)
          || null,
        first_registration_date: formData.first_registration_date || null,
        vehicle_category: formData.vehicle_category || null,
        tank_capacity_liters: parseInt(formData.tank_capacity_liters) || null,
        // Carte grise fields (from OCR scan)
        carte_grise_holder: formData.carte_grise_holder || null,
        carte_grise_date: formData.carte_grise_date || null,
        carte_grise_status: formData.carte_grise_status || null,
        // Auto-set CO2 homologation type
        co2_homologation_type: (parseInt(formData.co2_gkm) || parseInt(formData.catalog_co2))
          && formData.first_registration_date >= '2020-03-01' ? 'wltp'
          : (parseInt(formData.co2_gkm) || parseInt(formData.catalog_co2))
            && formData.first_registration_date >= '2004-06-01' ? 'nedc'
          : (parseInt(formData.puissance_fiscale) || parseInt(formData.catalog_fiscal_power)) ? 'pa' : 'unknown',
        // Auto-set electric flag
        electric_only_flag: (formData.fuel_type || '').toLowerCase() === 'electric' ? 1 : 0,
      };

      let response;
      let vehicleId;

      if (isEditMode) {
        response = await api.vehicle.update(vehicle.id, vehicleData);
        vehicleId = vehicle.id;
      } else {
        response = await api.vehicle.create(vehicleData);
        vehicleId = response.data?.id;

        // Create vehicle location if site was selected (only for new vehicles)
        if (vehicleId && formData.initial_location_id) {
          const locationData = {
            org_id: formData.org_id, vehicle_id: vehicleId,
            site_id: parseInt(formData.initial_location_id), assignment_type: 'permanent',
            assigned_at: new Date().toISOString().split('T')[0],
            is_current: 1, reason: 'Attribution initiale'
          };
          try {
            await api.vehicleLocation.create(locationData);
          } catch (locationError) {
            handleApiError(locationError, t('vehicles.wizard.messages.siteAssignmentContext'), { onMessage, title: t('common.messages.warningTitle') });
          }
        }
      }

      // Create initial resource readings (odometer, fuel, adblue)
      if (vehicleId && !isEditMode) {
        const odometer = parseInt(formData.initial_odometer_km) || 0;
        const now = new Date().toISOString();
        const readings = [];

        // Odometer is always recorded (even 0 = baseline)
        if (formData.initial_odometer_km) {
          readings.push({ resource_type: 'fuel_level', value_percent: formData.initial_fuel_level || 50, status: 'ok', source: 'manual', reading_date: now, odometer_km: odometer });
        }

        // Fuel/battery level
        if (formData.initial_fuel_level != null) {
          readings.push({ resource_type: 'fuel_level', value_percent: formData.initial_fuel_level, status: formData.initial_fuel_level <= 15 ? 'low' : 'ok', source: 'manual', reading_date: now, odometer_km: odometer });
        }

        // AdBlue for diesel
        if ((formData.fuel_type || '').toLowerCase() === 'diesel' && formData.initial_adblue_level != null) {
          readings.push({ resource_type: 'adblue_level', value_percent: formData.initial_adblue_level, status: formData.initial_adblue_level <= 15 ? 'low' : 'ok', source: 'manual', reading_date: now, odometer_km: odometer });
        }

        if (readings.length > 0) {
          try {
            await vehicleResourceAPI.createBulkReadings(vehicleId, readings, odometer);
          } catch (resError) {
            handleApiError(resError, t('vehicles.wizard.messages.initialReadingsContext'), { onMessage, title: t('common.messages.warningTitle') });
          }
        }
      }

      // Handle vehicle insurance links (for both new and edit mode)
      // Uses insuranceModuleAPI for coverage model-based assignment
      if (vehicleId && formData.insurance_assignments) {
        let assignments = [];
        try { assignments = JSON.parse(formData.insurance_assignments); } catch { assignments = []; }

        // Parse coverage model selection
        let coverageModel = {};
        try { coverageModel = JSON.parse(formData.insurance_coverage_model || '{}'); } catch { coverageModel = {}; }
        const coverageModelId = coverageModel.coverageModelId || null;

        // In edit mode, use smart sync: only delete removed records, update existing, create new
        if (isEditMode) {
          try {
            const existingInsurance = await api.vehicleInsurance.getByVehicle(vehicleId);
            const existingRecords = existingInsurance.data || [];

            const keepIds = new Set(
              assignments
                .filter(a => a.vehicleInsuranceId)
                .map(a => parseInt(a.vehicleInsuranceId))
            );

            // Soft-delete records that were explicitly removed by user
            for (const ins of existingRecords) {
              if (!keepIds.has(ins.id)) {
                await insuranceModuleAPI.removeVehicle(ins.id, {});
              }
            }

            // Update existing records
            for (const assignment of assignments) {
              if (assignment.vehicleInsuranceId && assignment.existingContractId) {
                const viId = parseInt(assignment.vehicleInsuranceId);
                const newContractId = parseInt(assignment.existingContractId);
                const premiumVal = assignment.premium !== '' && assignment.premium != null
                  ? parseFloat(assignment.premium) : null;
                const deductibleVal = assignment.franchise !== '' && assignment.franchise != null
                  ? parseFloat(assignment.franchise) : null;

                // Check if contract changed — use remove+reassign for proper side effects
                const existingVi = existingRecords.find(r => r.id === viId);
                if (existingVi && existingVi.contract_id !== newContractId) {
                  // Contract changed: remove from old, assign to new (triggers auto-remove, verification, premium sync)
                  await insuranceModuleAPI.removeVehicle(viId, {});
                  await insuranceModuleAPI.assignVehicle({
                    vehicle_id: existingVi.vehicle_id,
                    contract_id: newContractId,
                    premium_amount: premiumVal,
                    deductible_amount: deductibleVal,
                    coverage_model_id: coverageModelId,
                    overrides: buildFranchiseOverrides(assignment.franchise, coverageModel.coverageModelLevel, coverageModel.overrides),
                    assigned_at: formData.entry_date || new Date().toISOString().split('T')[0],
                    is_primary: existingVi.is_primary,
                  });
                } else {
                  // Same contract: update non-critical fields via basic CRUD + sync premium via module
                  const updateData = {
                    coverage_model_id: coverageModelId,
                    payment_frequency: assignment.paymentFrequency || 'annual',
                    payment_method: assignment.paymentMethod || 'direct_debit',
                    payment_day: parseInt(assignment.paymentDay) || 5,
                    contract_type: assignment.type,
                    custom_description: assignment.customDescription || null,
                  };
                  await api.vehicleInsurance.update(viId, updateData);

                  // Sync premium/deductible via module API (triggers _syncContractPremium)
                  try {
                    if (premiumVal != null) await insuranceModuleAPI.setVehiclePremium(viId, premiumVal);
                    if (deductibleVal != null) await insuranceModuleAPI.setVehicleDeductible(viId, deductibleVal);
                  } catch (syncErr) {
                    console.error('Error syncing premium/deductible via module API:', syncErr);
                  }
                }

                // Apply coverage model to contract (creates/updates coverage_blocks)
                if (coverageModelId && newContractId) {
                  try {
                    await insuranceModuleAPI.applyModel(newContractId, coverageModelId, buildFranchiseOverrides(assignment.franchise, coverageModel.coverageModelLevel, coverageModel.overrides));
                  } catch (modelErr) {
                    console.error('Error applying coverage model:', modelErr);
                  }
                }
              }
            }
          } catch (syncError) {
            console.error('Error syncing existing insurance:', syncError);
          }
        }

        // Create NEW insurance assignments via insuranceModuleAPI
        let isFirst = !isEditMode;
        for (const assignment of assignments) {
          if (isEditMode && assignment.vehicleInsuranceId) continue;

          if (assignment.existingContractId) {
            try {
              await insuranceModuleAPI.assignVehicle({
                vehicle_id: vehicleId,
                contract_id: parseInt(assignment.existingContractId),
                premium_amount: assignment.premium !== '' && assignment.premium != null
                  ? parseFloat(assignment.premium) : null,
                deductible_amount: assignment.franchise !== '' && assignment.franchise != null
                  ? parseFloat(assignment.franchise) : null,
                coverage_model_id: coverageModelId,
                overrides: buildFranchiseOverrides(assignment.franchise, coverageModel.coverageModelLevel, coverageModel.overrides),
                assigned_at: formData.entry_date || new Date().toISOString().split('T')[0],
                is_primary: isFirst,
                notes: assignment.customDescription || null,
              });
              isFirst = false;
            } catch (insuranceError) {
              console.error('Insurance assignment failed:', insuranceError);
              onMessage && onMessage({
                title: t('insurance.title'),
                message: t('vehicles.wizard.messages.insuranceLinkFailed', { error: insuranceError?.message || insuranceError }),
                type: 'error'
              });
            }
          }
          // NOTE: La branche `else if (assignment.createNew) { ... }` a été supprimée
          // le 2026-04-23 (Phase 4, ~137 lignes). La création d'un nouveau contrat passe
          // désormais par ContractForm ouvert en modal-over-modal depuis StepInsurance —
          // le contrat est persisté AVANT le submit véhicule, donc il arrive ici comme
          // un existingContractId standard. Un seul chemin de création = une seule
          // source de vérité. Voir ADR-009 et CLAUDE.md Trap #6.
        }
      }

      // Upload pending documents (for both new and edit mode)
      if (vehicleId && formData.pendingDocuments && formData.pendingDocuments.length > 0) {
        for (const doc of formData.pendingDocuments) {
          try {
            await documentAPI.upload(doc.file, {
              scope: 'VEHICLE', reference_id: vehicleId.toString(),
              document_type: doc.documentType, valid_to: null,
            });
          } catch (docError) { console.error('Document upload failed:', doc.fileName, docError); }
        }
      }

      // Upload pending scan file (carte grise from scanner)
      if (vehicleId && pendingScanFile?.file) {
        const referenceId = `VHC_${String(vehicleId).padStart(6, '0')}`;
        try {
          await documentAPI.upload(pendingScanFile.file, {
            scope: 'VEHICLE',
            reference_id: referenceId,
            document_type: 'VEHICLE_REGISTRATION',
            declared_data: JSON.stringify(pendingScanFile.declaredData),
          });
        } catch (err) {
          console.error('Post-create scan upload failed:', err);
        }
        setPendingScanFile(null);
      }

      // Show success modal for new vehicles
      if (!isEditMode && response.data) {
        setCreatedVehicle({
          id: response.data.id, make: formData.make, model: formData.model,
          license_plate: formData.license_plate, year: formData.year
        });
        setShowSuccessModal(true);
      } else {
        onMessage && onMessage({
          title: t('common.messages.successTitle'),
          message: t('vehicles.wizard.messages.vehicleUpdated', { make: formData.make, model: formData.model }),
          type: 'success'
        });
        onSuccess && onSuccess();
        onClose();
      }
    } catch (error) {
      handleApiError(error, isEditMode ? 'update' : 'create', { onMessage });
    }
  };

  // Submit handler for exit mode
  const handleExitSubmit = async () => {
    try {
      // Create vehicle exit record
      const exitData = {
        vehicle_id: vehicle.id,
        reason: formData.reason,
        other_nature_text: formData.other_nature_text || null,
        exit_effective_date: formData.exit_effective_date,
        insurance_mode: formData.insurance_mode,
        // Financial fields
        sale_price_eur: parseFloat(formData.sale_price_eur) || null,
        leasing_penalty_amount_eur: parseFloat(formData.leasing_penalty_amount_eur) || null,
        indemnity_amount_eur: parseFloat(formData.indemnity_amount_eur) || null,
        scrapyard_price_eur: parseFloat(formData.scrapyard_price_eur) || null,
        accounting_loss_eur: parseFloat(formData.accounting_loss_eur) || null,
        other_amount_eur: parseFloat(formData.other_amount_eur) || null,
        claim_id: formData.claim_id || null,
      };

      const response = await api.vehicleExit.create(exitData);
      const exitId = response.data?.id;

      if (!exitId) throw new Error('Failed to create vehicle exit record');

      // Store exitId for reference
      setCurrentExitId(exitId);

      // Create insurance links with their status
      if (formData.insurance_links && formData.insurance_links.length > 0) {
        for (const link of formData.insurance_links) {
          try {
            const createRes = await api.vehicleExit.addInsuranceLink(exitId, {
              vehicle_insurance_id: link.vehicle_insurance_id,
              contract_label: link.contract_label,
            });

            // If user marked termination as confirmed, update the link
            if (createRes.data?.id && (link.termination_confirmed || link.refund_received)) {
              const linkId = createRes.data.id;
              await api.vehicleExit.updateInsuranceLink(exitId, linkId, {
                termination_requested: link.termination_requested ? 1 : 0,
                termination_confirmed: link.termination_confirmed ? 1 : 0,
                refund_requested: link.refund_requested ? 1 : 0,
                refund_received: link.refund_received ? 1 : 0,
                refund_amount_eur: link.refund_amount_eur || null,
                claims_history_requested: link.claims_history_requested ? 1 : 0,
                claims_history_received: link.claims_history_received ? 1 : 0,
                notes: link.notes || '',
              });
            }
          } catch (linkError) {
            console.error('Insurance link creation failed:', linkError);
          }
        }
      }

      // Upload exit documents
      if (formData.pendingExitDocuments && formData.pendingExitDocuments.length > 0) {
        for (const doc of formData.pendingExitDocuments) {
          try {
            await documentAPI.upload(doc.file, {
              scope: 'VEHICLE_EXIT',
              reference_id: exitId,
              document_type: doc.documentType,
              valid_to: null,
            });
          } catch (docError) {
            console.error('Exit document upload failed:', doc.fileName, docError);
          }
        }
      }

      // Check if all validations pass to auto-complete
      const allInsuranceConfirmed = (formData.insurance_links || []).every(l => l.termination_confirmed);
      const hasRequiredDocs = (formData.pendingExitDocuments || []).some(d => d.documentType === 'CERFA_CESSION') &&
                              (formData.pendingExitDocuments || []).some(d => d.documentType === 'REGISTRATION_CANCELLED');

      if (allInsuranceConfirmed && hasRequiredDocs) {
        // Try to complete the exit
        try {
          const completeRes = await api.vehicleExit.complete(exitId);

          // Check if archive was created and show TCO report
          if (completeRes.archive?.archive_id) {
            setExitArchiveId(completeRes.archive.archive_id);
            setShowExitSuccessModal(true);
            // Don't close the workflow yet - the success modal will handle it
            return;
          } else {
            onMessage && onMessage({
              title: t('vehicles.wizard.messages.exitCompletedTitle'),
              message: t('vehicles.wizard.messages.exitCompletedMessage', { make: vehicle.make, model: vehicle.model }),
              type: 'success'
            });
          }
        } catch (completeError) {
          console.error('Could not auto-complete exit:', completeError);
          onMessage && onMessage({
            title: t('vehicles.wizard.messages.exitCreatedTitle'),
            message: t('vehicles.wizard.messages.exitCreatedMessage'),
            type: 'warning'
          });
        }
      } else {
        onMessage && onMessage({
          title: t('vehicles.wizard.messages.exitInitiatedTitle'),
          message: t('vehicles.wizard.messages.exitInitiatedMessage'),
          type: 'info'
        });
      }

      onSuccess && onSuccess();
      onClose();
    } catch (error) {
      handleApiError(error, t('vehicles.wizard.messages.vehicleExitContext'), { onMessage });
    }
  };

  const handleSubmit = () => {
    if (mode === 'exit') {
      handleExitSubmit();
    } else {
      handleEntrySubmit();
    }
  };

  // Validation for submit button
  const canSubmit = () => {
    if (mode === 'exit') {
      return formData.reason && formData.exit_effective_date;
    }
    return formData.make && formData.model && formData.license_plate && formData.vin;
  };

  // Handle success modal close
  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    setCreatedVehicle(null);
    onMessage && onMessage({
      title: t('common.messages.successTitle'),
      message: t('vehicles.wizard.messages.vehicleAdded', { make: formData.make, model: formData.model }),
      type: 'success'
    });
    onSuccess && onSuccess();
    onClose();
  };

  // Handle exit success modal close
  const handleExitSuccessModalClose = () => {
    setShowExitSuccessModal(false);
    setExitArchiveId(null);
    onSuccess && onSuccess();
    onClose();
  };

  // Render step content based on mode
  const renderStepContent = () => {
    if (mode === 'exit') {
      switch (currentStep) {
        case 1:
          return <StepExitContext formData={formData} updateFormData={updateFormData} vehicle={vehicle} />;
        case 2:
          return <StepExitFinancials formData={formData} updateFormData={updateFormData} vehicle={vehicle} />;
        case 3:
          return <StepExitDocuments formData={formData} updateFormData={updateFormData} />;
        case 4:
          return <StepExitInsurance formData={formData} updateFormData={updateFormData} vehicle={vehicle} exitId={currentExitId} />;
        case 5:
          return <StepExitSummary formData={formData} vehicle={vehicle} />;
        default:
          return null;
      }
    }

    // Entry mode steps
    switch (currentStep) {
      case 1:
        return (
          <StepIdentification
            formData={formData}
            updateFormData={updateFormData}
            showCatalogSelector={showCatalogSelector}
            setShowCatalogSelector={setShowCatalogSelector}
            handleCatalogSelect={handleCatalogSelect}
            showScanner={showScanner}
            setShowScanner={setShowScanner}
            onScanComplete={handleScanComplete}
            catalogVersions={catalogVersions}
            loadingVersions={loadingVersions}
          />
        );
      case 2:
        return <StepOwner formData={formData} updateFormData={updateFormData} tenant={tenant} suppliers={suppliers} />;
      case 3:
        return <StepFinancing formData={formData} updateFormData={updateFormData} />;
      case 4:
        return <StepDocuments formData={formData} updateFormData={updateFormData} isEditMode={isEditMode} pendingScanFile={pendingScanFile} />;
      case 5:
        return (
          <StepInsurance
            formData={formData}
            updateFormData={updateFormData}
            insuranceContracts={insuranceContracts}
            vehicleInsurances={vehicleInsurances}
            insuranceCompanies={insuranceCompanies}
            onContractCreated={handleContractCreated}
          />
        );
      case 6:
        return (
          <StepAssignments
            formData={formData}
            updateFormData={updateFormData}
            organizations={organizations}
            drivers={drivers}
            sites={sites}
          />
        );
      case 7:
        return (
          <StepResources
            formData={formData}
            updateFormData={updateFormData}
            isEditMode={isEditMode}
          />
        );
      case 8:
        return (
          <StepEntrySummary
            formData={formData}
            organizations={organizations}
            drivers={drivers}
            sites={sites}
            tenant={tenant}
            pendingScanFile={pendingScanFile}
          />
        );
      default:
        return null;
    }
  };

  const title = t(isEditMode ? config.editTitleKey : config.titleKey);
  const submitLabel = t(isEditMode ? config.editSubmitLabelKey : config.submitLabelKey);
  const steps = config.steps.map(({ labelKey, ...step }) => ({ ...step, label: t(labelKey) }));

  return (
    <>
      <WorkflowModal
        isOpen={isOpen}
        onClose={onClose}
        title={title}
        headerColor={config.accentColor}
      >
        {/* Step Indicator */}
        <WorkflowStepIndicator
          steps={steps}
          currentStep={currentStep}
          accentColor={config.accentColor}
        />

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {renderStepContent()}
        </div>

        {/* Footer */}
        <WorkflowFooter
          currentStep={currentStep}
          totalSteps={totalSteps}
          onPrevious={handlePrevious}
          onNext={handleNext}
          onSubmit={handleSubmit}
          canSubmit={canSubmit()}
          submitLabel={submitLabel}
          accentColor={config.accentColor}
        />
      </WorkflowModal>

      {/* Success Modal (entry mode only) */}
      {mode === 'entry' && (
        <VehicleCreatedSuccessModal
          isOpen={showSuccessModal}
          onClose={handleSuccessModalClose}
          vehicle={createdVehicle}
          isEditMode={isEditMode}
          onStartInspection={onStartInspection}
        />
      )}

      {/* Exit Success Modal with TCO Report */}
      {mode === 'exit' && (
        <VehicleExitSuccessModal
          isOpen={showExitSuccessModal}
          onClose={handleExitSuccessModalClose}
          vehicle={vehicle}
          archiveId={exitArchiveId}
        />
      )}
    </>
  );
}

export default VehicleLifecycleWorkflow;
